import { memo } from 'react';

import { Handle, Position, type NodeProps } from '@xyflow/react';
import { ArrowDown, ArrowUp, Minus, TrendingUp } from 'lucide-react';

import { cn } from '@/lib/utils';

export type SubItemData = {
  label: string;
  count: number;
  change: number | null;
  pctOfParent: number;
  colorClass: string;
  classification: string;
  /** v2 drilldown: explicit metric key for this sub-item — passed to the
   *  click handler so the drilldown filter doesn't have to look it up by
   *  `classification` (which isn't unique when multiple nodes share `all`). */
  metricKey?: string;
  isSelected: boolean;
};

export type MindMapNodeData = {
  label: string;
  count: number;
  change: number | null;
  inbound: number;
  outbound: number;
  colorClass: string;
  showDirection: boolean;
  classification: string;
  /** v2 drilldown: explicit metric key for this node. See SubItemData.metricKey. */
  metricKey?: string;
  isSelected: boolean;
  isRoot?: boolean;
  pctOfParent: number;
  parentCount: number | null;
  subItems?: SubItemData[];
  onSubItemClick?: (classification: string, metricKey?: string) => void;
  tooltip?: string;
  isEditing?: boolean;
  isHighlighted?: boolean;
};

export const ACCENT: Record<string, string> = {
  blue: '#3b82f6',
  green: '#10b981',
  amber: '#f59e0b',
  red: '#f43f5e',
  purple: '#8b5cf6',
  slate: '#64748b',
  teal: '#14b8a6',
  orange: '#f97316',
};

// ─── tiny helpers ──────────────────────────────────────────────────────────────
function ChangeBadge({ change }: { change: number | null }) {
  if (change === null) return null;
  if (change > 0)
    return (
      <span className="flex items-center gap-0.5 rounded-full bg-emerald-500/15 px-1.5 py-[2px] text-[9px] font-bold text-emerald-400">
        <ArrowUp className="h-2 w-2" />
        {change.toFixed(1)}%
      </span>
    );
  if (change < 0)
    return (
      <span className="flex items-center gap-0.5 rounded-full bg-rose-500/15 px-1.5 py-[2px] text-[9px] font-bold text-rose-400">
        <ArrowDown className="h-2 w-2" />
        {Math.abs(change).toFixed(1)}%
      </span>
    );
  return (
    <span className="text-muted-foreground bg-muted flex items-center gap-0.5 rounded-full px-1.5 py-[2px] text-[9px] font-bold">
      <Minus className="h-2 w-2" />
      0%
    </span>
  );
}

function PctBar({ pct, accent }: { pct: number; accent: string }) {
  return (
    <div className="bg-muted mt-2 h-[2px] w-full overflow-hidden rounded-full">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{ width: `${Math.min(pct, 100)}%`, background: `${accent}99` }}
      />
    </div>
  );
}

// ─── tooltip descriptions ─────────────────────────────────────────────────────
export const TOOLTIPS: Record<string, string> = {
  all: 'Every inbound and outbound call recorded in the selected date range.',
  answered: 'Calls where the customer connected with an agent and the call was picked up.',
  not_answered:
    'Calls that never reached an agent — includes no-answer, busy, and voicemail outcomes.',
  engaged:
    'Answered calls lasting more than 25 seconds, indicating a meaningful conversation took place.',
  immediate_hangup:
    'Answered calls lasting 25 seconds or less — short calls that did not progress to an engaged conversation.',
  sales_pipeline:
    'Engaged calls with a LEAD_CAPTURED outcome. This is the sales pipeline head and does not require buying-intent scoring.',
  inquiries:
    'Engaged calls that are not LEAD_CAPTURED, including PICKED_UP (>25s) and inquiry outcomes.',
  no_answer: 'The call rang but was not picked up on either end within the timeout period.',
  busy: 'The call could not connect because the destination line was busy.',
  voicemail: 'The call was redirected to, or ended in, voicemail.',
  vehicle_chosen:
    'The caller identified a specific vehicle they were interested in buying during the call.',
  finance: 'The caller enquired about or requested finance options for a vehicle purchase.',
  outright: 'The caller expressed intent to purchase a vehicle outright without financing.',
  link_sent: 'A deposit or payment link was sent to the caller during or after the call.',
  link_clicked: 'The caller clicked the payment or deposit link that was sent to them.',
  finance_link_sent: 'A finance application link was sent to the caller during or after the call.',
  finance_link_clicked: 'The caller clicked the finance application link that was sent to them.',
  showroom_booked:
    'A physical showroom visit or test-drive appointment was booked during the call.',
  callback_requested: 'The caller asked to be called back at a more convenient time.',
  already_purchased:
    'The caller had already bought a vehicle — a post-sale customer service enquiry.',
  finance_inquiry: 'The caller had a question about an existing or potential finance agreement.',
  refund_request: 'The caller requested a refund or raised a payment dispute.',
  administrative: 'A general admin call with no sales intent or specific inquiry category.',
};

// ─── tooltip bubble ─────────────────────────────────────────────────────────────────
function NodeTooltip({ text }: { text: string }) {
  return (
    <div className="bg-popover pointer-events-none absolute bottom-[calc(100%+10px)] left-1/2 z-[999] w-60 -translate-x-1/2 rounded-xl border px-3.5 py-2.5 opacity-0 shadow-2xl backdrop-blur-md transition-opacity duration-200 group-hover:opacity-100">
      <p className="text-popover-foreground/75 text-[11px] leading-relaxed">{text}</p>
      <div className="border-t-popover absolute top-full left-1/2 h-0 w-0 -translate-x-1/2 border-x-4 border-t-4 border-x-transparent" />
    </div>
  );
}

function SubTooltip({ text }: { text: string }) {
  return (
    <div className="bg-popover pointer-events-none absolute top-1/2 left-[calc(100%+10px)] z-[999] w-56 -translate-y-1/2 rounded-xl border px-3.5 py-2.5 opacity-0 shadow-2xl backdrop-blur-md transition-opacity duration-200 group-hover/sub:opacity-100">
      <p className="text-popover-foreground/75 text-[11px] leading-relaxed">{text}</p>
      <div className="border-r-popover absolute top-1/2 right-full h-0 w-0 -translate-y-1/2 border-y-4 border-r-4 border-y-transparent" />
    </div>
  );
}

// ─── main node card ─────────────────────────────────────────────────────────────
export const MindMapNode = memo(({ data }: NodeProps) => {
  const d = data as MindMapNodeData;
  const accent = ACCENT[d.colorClass] ?? ACCENT.slate;
  const hasSubItems = d.subItems && d.subItems.length > 0;
  const cardW = d.isRoot ? 300 : 240;
  const tooltip = TOOLTIPS[d.classification] ?? d.tooltip;

  return (
    <div
      style={{
        width: cardW,
        ...(d.isSelected
          ? { boxShadow: `0 0 0 1.5px ${accent}, 0 0 28px ${accent}44` }
          : { boxShadow: '0 4px 24px rgba(0,0,0,0.55)' }),
      }}
      className={cn(
        'bg-card group relative cursor-pointer overflow-visible rounded-2xl border transition-all duration-150 select-none',
        d.isSelected ? 'scale-[1.03]' : 'hover:border-primary/30 hover:scale-[1.02]',
        d.isHighlighted && 'animate-pulse ring-2 ring-blue-500 ring-offset-2',
      )}
    >
      {tooltip && <NodeTooltip text={tooltip} />}
      {/* top accent line */}
      <div
        className="h-[3px] w-full rounded-t-2xl"
        style={{ background: `linear-gradient(90deg,${accent},${accent}22)` }}
      />

      <Handle
        type="target"
        position={Position.Left}
        isConnectable
        style={{
          background: accent,
          border: 'none',
          width: d.isEditing ? 18 : 7,
          height: d.isEditing ? 18 : 7,
          left: d.isEditing ? -9 : -4,
          boxShadow: d.isEditing ? `0 0 0 2px ${accent}` : `0 0 6px ${accent}`,
          transition: 'all 0.2s ease',
          zIndex: 10,
        }}
        className={cn(
          d.isEditing && '!bg-background cursor-crosshair !border-2',
          d.isEditing ? `!border-[${accent}]` : '',
        )}
      />

      {/* ── header ── */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-start justify-between gap-2">
          <span
            className={cn(
              'leading-tight font-bold uppercase',
              d.isRoot
                ? 'text-foreground/70 text-[15px] tracking-[0.08em]'
                : 'text-muted-foreground text-[12px] tracking-[0.1em]',
            )}
          >
            {d.label}
          </span>
          <ChangeBadge change={d.change} />
        </div>

        {/* big number */}
        <div className="mt-1 flex items-baseline gap-2">
          <span
            className={cn(
              'text-foreground leading-none font-black tracking-tight tabular-nums',
              d.isRoot ? 'text-[52px]' : 'text-[30px]',
            )}
            style={{ textShadow: `0 0 20px ${accent}66` }}
          >
            {d.count.toLocaleString()}
          </span>
          {d.parentCount !== null && d.pctOfParent > 0 && (
            <span className="text-[25px] font-semibold" style={{ color: `${accent}cc` }}>
              {d.pctOfParent}%
            </span>
          )}
        </div>

        {/* progress bar = share of parent */}
        {d.parentCount !== null && <PctBar pct={d.pctOfParent} accent={accent} />}

        {/* inbound / outbound */}
        {d.showDirection && (d.inbound > 0 || d.outbound > 0) && (
          <div className="border-border mt-2 flex items-center gap-3 border-t pt-2">
            <span className="text-muted-foreground flex items-center gap-1 text-[10px] font-medium">
              <span style={{ color: accent }} className="text-[10px]">
                ↓
              </span>
              {d.inbound.toLocaleString()}
            </span>
            <span className="text-muted-foreground/30 text-[10px]">·</span>
            <span className="text-muted-foreground flex items-center gap-1 text-[10px] font-medium">
              <span style={{ color: accent }} className="text-[10px]">
                ↑
              </span>
              {d.outbound.toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* ── sub-items ── */}
      {hasSubItems && (
        <div className="border-border border-t px-3 pt-2 pb-2.5">
          <div className="mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="text-muted-foreground/50 h-3 w-3" />
            <span className="text-muted-foreground/50 text-[9px] font-bold tracking-[0.1em] uppercase">
              Breakdown
            </span>
          </div>
          <div className="space-y-[3px]">
            {d.subItems!.map((sub) => {
              const subAccent = ACCENT[sub.colorClass] ?? accent;
              return (
                <div
                  key={sub.metricKey ?? sub.classification}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation();
                    d.onSubItemClick?.(sub.classification, sub.metricKey);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.stopPropagation();
                      d.onSubItemClick?.(sub.classification, sub.metricKey);
                    }
                  }}
                  style={
                    sub.isSelected
                      ? { background: `${subAccent}18`, borderColor: `${subAccent}66` }
                      : {}
                  }
                  className={cn(
                    'group/sub relative flex cursor-pointer items-center justify-between rounded-lg px-2.5 py-[5px]',
                    'border border-transparent transition-colors',
                    sub.isSelected ? '' : 'hover:border-border hover:bg-muted/50',
                  )}
                >
                  {TOOLTIPS[sub.classification] && (
                    <SubTooltip text={TOOLTIPS[sub.classification]} />
                  )}
                  {/* coloured left strip */}
                  <div className="flex min-w-0 items-center gap-2">
                    <div
                      className="h-[18px] w-[2.5px] shrink-0 rounded-full"
                      style={{ background: subAccent }}
                    />
                    <span className="text-foreground/65 truncate text-[10px] leading-tight font-medium">
                      {sub.label}
                    </span>
                  </div>
                  <div className="ml-2 flex shrink-0 items-center gap-1.5">
                    {sub.pctOfParent > 0 && (
                      <span
                        className="text-[13px] font-semibold"
                        style={{ color: `${subAccent}aa` }}
                      >
                        {sub.pctOfParent}%
                      </span>
                    )}
                    <span className="text-foreground text-[11px] font-bold tabular-nums">
                      {sub.count.toLocaleString()}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Handle
        type="source"
        position={Position.Right}
        isConnectable
        style={{
          background: accent,
          border: 'none',
          width: d.isEditing ? 18 : 7,
          height: d.isEditing ? 18 : 7,
          right: d.isEditing ? -9 : -4,
          boxShadow: d.isEditing ? `0 0 0 2px ${accent}` : `0 0 6px ${accent}`,
          transition: 'all 0.2s ease',
          zIndex: 10,
        }}
        className={cn(
          d.isEditing && '!bg-background cursor-crosshair !border-2',
          d.isEditing ? `!border-[${accent}]` : '',
        )}
      />
    </div>
  );
});

MindMapNode.displayName = 'MindMapNode';
